import { policyUsesApprovalGroups, policyUsesEscalationRules } from "@veritrellis/shared";
import type { PlanFeatures } from "../../lib/planFeatures.js";
import type { BuilderState } from "./builderTypes.js";
import { serializeToPolicy } from "./policySerializer.js";

export type ValidationResult = {
  severity: "error" | "warning" | "info";
  message: string;
};

export const validateBuilderState = (
  state: BuilderState,
  planFeatures?: PlanFeatures
): ValidationResult[] => {
  const results: ValidationResult[] = [];

  if (state.mode !== "conditional") {
    return results;
  }

  if (state.rules.length === 0) {
    results.push({
      severity: "warning",
      message: "No rules defined. All requests will fall through to the default decision."
    });
    return results;
  }

  for (let i = 0; i < state.rules.length; i++) {
    const rule = state.rules[i]!;

    if (rule.conditions.length === 0) {
      results.push({
        severity: "error",
        message: `Rule ${i + 1} has no conditions. Add at least one condition or remove this rule.`
      });
    }

    if (rule.decision === "require_approval" && !rule.approverGroupId) {
      results.push({
        severity: "warning",
        message: `Rule ${i + 1} requires approval but no approver group is selected.`
      });
    }

    if (rule.decision === "require_modified_parameters" && rule.modifications == null) {
      results.push({
        severity: "warning",
        message: `Rule ${i + 1} requires modified parameters but no modifications are defined.`
      });
    }
  }

  if (planFeatures !== undefined) {
    const policy = serializeToPolicy(state);
    if (policyUsesApprovalGroups(policy) && !planFeatures.approvalGroups) {
      results.push({
        severity: "error",
        message:
          "This policy assigns an approver group, which requires Pro or Enterprise. Clear approver groups on rules or upgrade your plan."
      });
    }
    if (policyUsesEscalationRules(policy) && !planFeatures.escalationRules) {
      results.push({
        severity: "error",
        message:
          "This policy has more than one rule (escalation ladder), which requires Pro or Enterprise. Remove extra rules or upgrade your plan."
      });
    }
  }

  return results;
};
